import { useRef, useState } from 'react';
import { isGameKey, liveSnapshot, type Snapshot } from '@/game/profiles';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card } from './ui';
import { copyText, download, fmtBytes, useToast } from './util';

type Kind = 'game' | 'profiles' | 'debug' | 'qa' | 'other';

function kindOf(key: string): Kind {
  if (isGameKey(key)) return 'game';
  if (key.startsWith('foe-names.profile')) return 'profiles';
  if (key === 'foe-names.debug' || key.startsWith('foe-names.audiolab')) return 'debug';
  if (key.startsWith('foe-names.qa')) return 'qa';
  return 'other';
}

const BADGE: Record<Kind, string | undefined> = {
  game: s.badgeOk,
  profiles: s.badgeAccent,
  debug: s.badgeWarn,
  qa: undefined,
  other: undefined,
};

function readAll(): { key: string; value: string }[] {
  try {
    return Object.keys(localStorage)
      .sort()
      .map((key) => ({ key, value: localStorage.getItem(key) ?? '' }));
  } catch {
    return [];
  }
}

function pretty(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/**
 * This device's saved data (localStorage): every key with its size and kind, editable and
 * deletable, and the active player's game state as a JSON file to export or import.
 */
export function StoragePanel() {
  const toast = useToast();
  const [entries, setEntries] = useState(readAll);
  const [open, setOpen] = useState<{ key: string; text: string } | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const refresh = () => {
    setEntries(readAll());
  };
  const total = entries.reduce((sum, e) => sum + (e.key.length + e.value.length) * 2, 0);

  const save = () => {
    if (!open) return;
    try {
      localStorage.setItem(open.key, open.text);
      toast(`Saved ${open.key} (reload to apply)`);
      setOpen(null);
      refresh();
    } catch (e) {
      toast(`Not saved: ${String(e)}`);
    }
  };

  const importState = async (f: File) => {
    try {
      const data = JSON.parse(await f.text()) as Snapshot;
      if (typeof data !== 'object' || Array.isArray(data)) throw new Error('not an object');
      for (const key of Object.keys(localStorage)) if (isGameKey(key)) localStorage.removeItem(key);
      for (const [key, value] of Object.entries(data))
        if (isGameKey(key) && typeof value === 'string') localStorage.setItem(key, value);
      window.history.replaceState(null, '', '#/debug/storage');
      window.location.reload();
    } catch (e) {
      toast(`Import failed: ${String(e)}`);
    }
  };

  return (
    <>
      <Card title="Game state">
        <div className={s.row}>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              download(
                `foe-names-state-${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.json`,
                JSON.stringify(liveSnapshot(), null, 2),
              );
            }}
          >
            Export JSON
          </button>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              void copyText(JSON.stringify(liveSnapshot())).then((ok) => {
                toast(ok ? 'Copied the game state' : 'Copy failed');
              });
            }}
          >
            Copy
          </button>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              file.current?.click();
            }}
          >
            Import JSON…
          </button>
          <input
            ref={file}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importState(f);
              e.target.value = '';
            }}
          />
        </div>
        <p className={s.hint}>
          The active player's keys only. Importing replaces them and reloads.
        </p>
      </Card>

      <Card
        title={`localStorage (${String(entries.length)} keys, ${fmtBytes(total)})`}
        actions={
          <button type="button" className={cx(s.btn, s.small)} onClick={refresh}>
            Refresh
          </button>
        }
        flush
      >
        {entries.length === 0 ? (
          <p className={s.empty}>Nothing saved (or storage is blocked).</p>
        ) : (
          <div className={s.tableWrap}>
            <table className={cx(s.table, s.stack)}>
              <thead>
                <tr>
                  <th>Key</th>
                  <th className={s.hideNarrow}>Value</th>
                  <th className={s.num}>Size</th>
                  <th>
                    <span className={s.visuallyHidden}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map(({ key, value }) => {
                  const kind = kindOf(key);
                  const editing = open?.key === key;
                  return (
                    <FragmentRows key={key}>
                      <tr className={cx(editing && s.current)}>
                        <td>
                          <span className={s.mono}>{key}</span>{' '}
                          <span className={cx(s.badge, BADGE[kind])}>{kind}</span>
                        </td>
                        <td
                          data-label="Value"
                          className={cx(s.hideNarrow, s.mono, s.truncate)}
                          title={value}
                        >
                          {value}
                        </td>
                        <td data-label="Size" className={cx(s.num, s.mono)}>
                          {fmtBytes(value.length * 2)}
                        </td>
                        <td>
                          <div
                            className={s.row}
                            style={{ flexWrap: 'nowrap', justifyContent: 'flex-end' }}
                          >
                            {confirm === key ? (
                              <>
                                <button
                                  type="button"
                                  className={cx(s.btn, s.small, s.dangerSolid)}
                                  onClick={() => {
                                    localStorage.removeItem(key);
                                    setConfirm(null);
                                    refresh();
                                    toast(`Deleted ${key}`);
                                  }}
                                >
                                  Delete
                                </button>
                                <button
                                  type="button"
                                  className={cx(s.btn, s.small)}
                                  onClick={() => {
                                    setConfirm(null);
                                  }}
                                >
                                  Keep
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className={cx(s.btn, s.small)}
                                  aria-expanded={editing}
                                  onClick={() => {
                                    setOpen(editing ? null : { key, text: pretty(value) });
                                  }}
                                >
                                  {editing ? 'Close' : 'Edit'}
                                </button>
                                <button
                                  type="button"
                                  className={cx(s.btn, s.small, s.danger)}
                                  aria-label={`Delete ${key}`}
                                  onClick={() => {
                                    setConfirm(key);
                                  }}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {editing && (
                        <tr className={s.current}>
                          <td colSpan={4}>
                            <textarea
                              className={s.textarea}
                              aria-label={`Value of ${key}`}
                              value={open.text}
                              spellCheck={false}
                              onChange={(e) => {
                                setOpen({ key, text: e.target.value });
                              }}
                            />
                            <div className={s.row} style={{ marginTop: 6 }}>
                              <button
                                type="button"
                                className={cx(s.btn, s.small, s.primary)}
                                onClick={save}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className={cx(s.btn, s.small)}
                                onClick={() => {
                                  window.location.reload();
                                }}
                              >
                                Reload page
                              </button>
                              <span className={s.hint}>
                                Stores read their keys at start: reload after saving.
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </FragmentRows>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function FragmentRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
